/**
 * PlanContext — app-level subscription/plan state for the owner app.
 *
 * Fetches `GET /api/billing/me` ONCE when the authenticated owner layout mounts (a JWT is
 * guaranteed there via PrivateRoute) and exposes the venue's plan + feature flags to the whole
 * authenticated tree. This is the single client-side source of truth that gates UI affordances
 * (locked sidebar entries, upsell prompts) — but it is NEVER the security boundary: every gated
 * action is also enforced server-side (e.g. dashboard.service strips analytics for Starter).
 *
 * Feature flags mirror node/_helpers/plan-limits.js featuresFor(): analytics, pos, removeBranding,
 * onlinePayment, multiVenue.
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import API from '../api/Api';

const DEFAULT = {
    loading: true, plan: null, planLabel: null, features: null, limits: null, usage: null,
    isTrial: false, trialExpired: false, planExpiresAt: null,
};

const PlanContext = createContext({ ...DEFAULT, refresh: () => {} });

export const PlanProvider = ({ children }) => {
    const [state, setState] = useState(DEFAULT);
    // "Open for You" (Rajat): free, unlimited, PERSISTENT unlock of a Pro feature for this venue.
    // localUnlocks gives an instant optimistic reveal; the POST persists it server-side (tbl_venue
    // .opened_features) so featuresFor() OR-s it onto the plan and gated ACTIONS (POS Pay-Bill,
    // analytics data) transact for real. refresh() then pulls the updated feature flags.
    const [localUnlocks, setLocalUnlocks] = useState(() => new Set());

    const refresh = useCallback(() => {
        setState(s => ({ ...s, loading: true }));
        API.getData('billing/me')
            .then(res => {
                const d = res?.data?.data;
                if (!d) {
                    setState({ ...DEFAULT, loading: false });
                    return;
                }
                setState({
                    loading: false,
                    plan: d.plan,
                    planLabel: d.plan_label,
                    features: d.features || null,
                    limits: d.limits || null,
                    usage: d.usage || null,
                    isTrial: !!d.is_trial,
                    trialExpired: !!d.trial_expired,
                    planExpiresAt: d.plan_expires_at || null,
                });
            })
            .catch(() => setState({ ...DEFAULT, loading: false }));
    }, []);

    const openForYou = useCallback((name) => {
        setLocalUnlocks((prev) => { const next = new Set(prev); next.add(name); return next; }); // instant reveal
        API.postData('billing/open-for-you', { feature: name })
            .then(() => refresh())   // persist → server feature flags now include it
            .catch(() => { /* keep the optimistic reveal even if the persist call fails */ });
    }, [refresh]);

    useEffect(() => { refresh(); }, [refresh]);

    return (
        <PlanContext.Provider value={{ ...state, refresh, localUnlocks, openForYou }}>
            {children}
        </PlanContext.Provider>
    );
};

export const usePlan = () => useContext(PlanContext);

/**
 * Whether a named feature is unlocked on the current plan.
 * Defaults to TRUE while the plan is loading / unknown so a paying customer never flashes a
 * lock before their plan resolves. The authoritative gate is always the backend.
 */
export const useFeature = (name) => {
    const { features, loading, localUnlocks } = usePlan();
    if (localUnlocks && localUnlocks.has(name)) return true; // "Open for You" in-session reveal
    if (loading || !features) return true;
    return !!features[name];
};

export default PlanContext;
